import graphql__c433347a165dd0942dfe from "./__generated__/DetailedPaymentMethodList_customerAccount.graphql.ts";import * as React from "react";
import {graphql} from "react-relay";
import {useFragment} from "react-relay/hooks";
import {faStar, faTrashCan, faPencil} from "@fortawesome/pro-regular-svg-icons";
import ActionButton from "./presentational/button/ActionButton";
import {connect, ConnectedProps} from "react-redux";
import {hideModal, showModal} from "../store/actions";
import DeletePaymentMethodDialog from "./dialog/DeletePaymentMethodDialog";
import SetDefaultPaymentMethodDialog from "./dialog/SetDefaultPaymentMethodDialog";
import UpdatePaymentTermsDialog from "./dialog/UpdatePaymentTermsDialog";
import {useIntl} from "react-intl";
import PaymentMethodIcon from "./presentational/PaymentMethodIcon";

type Props = {
    queryRef: any
    typeNameFilter?: string
}

const connector = connect(undefined, {
    dispatchShowModal: showModal,
    dispatchHideModal: hideModal
});

type DetailedPaymentMethodListProps = Props & ConnectedProps<typeof connector>;

const DetailedPaymentMethodList: React.FC<DetailedPaymentMethodListProps> = (props) => {
    const intl = useIntl();
    const {dispatchShowModal, dispatchHideModal} = props;
    const customerAccount = useFragment(
        graphql__c433347a165dd0942dfe,
        props.queryRef
    );

    const showSetDefaultPaymentMethodDialog = (paymentMethod) => {
        dispatchShowModal({
            body: (<SetDefaultPaymentMethodDialog customerAccount={customerAccount} paymentMethod={paymentMethod} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const showDeletePaymentMethodDialog = (paymentMethod) => {
        dispatchShowModal({
            body: (<DeletePaymentMethodDialog paymentMethod={paymentMethod} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    const showUpdatePaymentTermsDialog = (paymentMethod) => {
        dispatchShowModal({
            body: (<UpdatePaymentTermsDialog paymentMethodIdentifier={paymentMethod.id} currentPaymentTerms={paymentMethod.paymentTerms} handleCloseDialog={dispatchHideModal}/>)
        })
    };

    return (
        <>
            <table className="min-w-full divide-y divide-gray-600">
                <thead>
                    <tr>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-8"
                        >
                            {intl.formatMessage({id: 'list.paymentMethod.header.type'})}
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                            {intl.formatMessage({id: 'list.paymentMethod.header.number'})}
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                            {intl.formatMessage({id: 'list.paymentMethod.header.details'})}
                        </th>
                        <th
                            scope="col"
                            className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                        >
                            {intl.formatMessage({id: 'list.paymentMethod.header.status'})}
                        </th>
                        <th scope="col" className="relative px-6 py-3">
                            <span className="sr-only">{intl.formatMessage({id: 'button.edit'})}</span>
                        </th>
                    </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                    {customerAccount.paymentMethods.map((paymentMethod, index) => {
                        if (!paymentMethod) {
                            return (<></>);
                        }

                        let paymentMethodType;
                        let paymentTerms;

                        switch (paymentMethod.paymentMethodType) {
                            case 'Card': {
                                switch (paymentMethod.cardBrand) {
                                    case 'Visa':
                                        paymentMethodType = intl.formatMessage({id: 'paymentMethod.Card.visa'});
                                        break;
                                    case 'Mastercard':
                                        paymentMethodType = intl.formatMessage({id: 'paymentMethod.Card.mastercard'});
                                        break;
                                    default:
                                        paymentMethodType = intl.formatMessage({id: 'paymentMethod.Card'});
                                        break;
                                }
                                break;
                            }
                            case 'SepaDebit': {
                                paymentMethodType = intl.formatMessage({id: 'paymentMethod.SepaDebit'});
                                break;
                            }
                            case 'OnAccount': {
                                paymentMethodType = intl.formatMessage({id: 'paymentMethod.OnAccount'});
                                break;
                            }
                            default: {
                                paymentMethodType = intl.formatMessage({id: 'paymentMethod.unknown'});
                            }
                        }

                        // Calculate payment terms display for all payment methods
                        if (paymentMethod.paymentTerms) {
                            switch (paymentMethod.paymentTerms) {
                                case 'UponReceipt':
                                    paymentTerms = intl.formatMessage({id: 'paymentMethod.paymentTerms.uponReceipt'});
                                    break;
                                case 'Net7':
                                    paymentTerms = intl.formatMessage({id: 'paymentMethod.paymentTerms.net7'});
                                    break;
                                case 'Net15':
                                    paymentTerms = intl.formatMessage({id: 'paymentMethod.paymentTerms.net15'});
                                    break;
                                case 'Net30':
                                    paymentTerms = intl.formatMessage({id: 'paymentMethod.paymentTerms.net30'});
                                    break;
                                case 'Net60':
                                    paymentTerms = intl.formatMessage({id: 'paymentMethod.paymentTerms.net60'});
                                    break;
                            }
                        }

                        return (
                            <tr key={index}>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900">
                                        <PaymentMethodIcon
                                            paymentMethodType={paymentMethod.paymentMethodType}
                                            cardBrand={paymentMethod.cardBrand}
                                            className="h-6 w-6"
                                        />
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    <div className="text-sm text-gray-900" title={paymentMethod.id}>
                                        {paymentMethodType}
                                    </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {paymentMethod.lastFourDigits && (
                                        <div className="text-sm text-gray-900">•••• {paymentMethod.lastFourDigits}</div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                    {paymentMethod.paymentMethodType === "Card" && (
                                        <div className="text-sm text-gray-900">
                                            {paymentMethod.expirationDate}
                                        </div>
                                    )}
                                    {paymentMethod.paymentMethodType === "SepaDebit" && (
                                        <div className="text-sm text-gray-900">
                                            {paymentMethod.mandateReference}
                                        </div>
                                    )}
                                    {paymentTerms && (
                                        <div className="text-sm text-gray-500 mt-1">
                                            {paymentTerms}
                                        </div>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                    {customerAccount.defaultPaymentMethod?.id === paymentMethod.id && (
                                        <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">{intl.formatMessage({id: 'list.paymentMethod.status.default'})}</span>
                                    )}
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                    {customerAccount.defaultPaymentMethod?.id !== paymentMethod.id && (
                                        <ActionButton onClick={() => {
                                            showSetDefaultPaymentMethodDialog(paymentMethod)
                                        }} icon={faStar}/>
                                    )}
                                    <ActionButton onClick={() => {
                                        showUpdatePaymentTermsDialog(paymentMethod)
                                    }} icon={faPencil}/>
                                    <ActionButton onClick={() => {
                                        showDeletePaymentMethodDialog(paymentMethod)
                                    }} icon={faTrashCan}/>
                                </td>
                            </tr>
                        )
                    })}
                </tbody>
            </table>
        </>
    )
}

export default connector(DetailedPaymentMethodList)
